import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import { appConfig } from "../config";
import { winnersCopy } from "../config/appCopy";
import { calculatePrizePool } from "../data/prizePool";
import type { CountryRow } from "../data/types";
import { useData } from "../data/useData";
import { usePersistentString } from "../data/usePersistentString";
import { usePolymarketOdds } from "../data/usePolymarketOdds";
import { Flag } from "./Flag";
import { PolymarketEmbed } from "./PolymarketEmbed";

type SortField = "country" | "player" | "odds";
type SortDir = "asc" | "desc";
interface WinnersSort {
  field: SortField;
  dir: SortDir;
}

const SORT_FIELDS: SortField[] = ["country", "player", "odds"];
const DEFAULT_SORT: WinnersSort = { field: "odds", dir: "desc" };
const DEFAULT_DIR: Record<SortField, SortDir> = {
  country: "asc",
  player: "asc",
  odds: "desc",
};

export function Winners(): JSX.Element {
  const { countries = [], loading, error } = useData();
  const slugs = countries
    .map((country) => country.market?.trim())
    .filter((slug): slug is string => Boolean(slug));
  const { odds } = usePolymarketOdds(slugs);
  const [sortRaw, setSortRaw] = usePersistentString(
    appConfig.winnersSortStorageKey,
    "odds:desc",
  );
  const errorOrLoading = renderErrorOrLoading({ loading, error });

  if (errorOrLoading) return errorOrLoading;

  const sort = parseSort(sortRaw);
  const pool = calculatePrizePool(countries);
  const sorted = sortWinners(countries, odds, sort);

  const onSort = (field: SortField): void => {
    const dir: SortDir =
      field === sort.field
        ? sort.dir === "asc"
          ? "desc"
          : "asc"
        : DEFAULT_DIR[field];
    setSortRaw(`${field}:${dir}`);
  };

  return (
    <Stack spacing={3}>
      <PrizePoolBanner playerCount={pool.playerCount} total={pool.total} />
      <TableContainer component={Paper} sx={{ width: "100%" }}>
        <Table aria-label="winners table">
          <TableHead>
            <TableRow>
              <SortHeader field="country" label={winnersCopy.columnHeaders.country} sort={sort} onSort={onSort} />
              <SortHeader field="player" label={winnersCopy.columnHeaders.player} sort={sort} onSort={onSort} />
              <SortHeader field="odds" label={winnersCopy.columnHeaders.odds} sort={sort} onSort={onSort} />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((country, index) => (
              <WinnerRow
                key={country.code ?? country.name ?? `country-${index}`}
                country={country}
                yesPrice={
                  country.market ? odds.get(country.market.trim()) : undefined
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function SortHeader({
  field,
  label,
  sort,
  onSort,
}: {
  field: SortField;
  label: string;
  sort: WinnersSort;
  onSort: (field: SortField) => void;
}): JSX.Element {
  const active = sort.field === field;
  return (
    <TableCell sortDirection={active ? sort.dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : DEFAULT_DIR[field]}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function WinnerRow({
  country,
  yesPrice,
}: {
  country: CountryRow;
  yesPrice?: number;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const name = country.name ?? country.code ?? "";
  const player = country.assignedTo?.trim() || winnersCopy.unassignedPlayer;
  const oddsLabel =
    yesPrice !== undefined
      ? `${Math.round(yesPrice * 100)}%`
      : winnersCopy.oddsUnavailable;
  const canExpand = Boolean(country.market?.trim());

  return (
    <>
      <TableRow>
        <TableCell>
          <Flag
            country={country.code}
            style={{
              borderRadius: 2,
              display: "inline-block",
              height: 24,
              marginRight: 8,
              objectFit: "cover",
              position: "relative",
              verticalAlign: "middle",
              width: 40,
            }}
          />
          {name}
        </TableCell>
        <TableCell>{player}</TableCell>
        <TableCell>{oddsLabel}</TableCell>
        <TableCell padding="checkbox">
          {canExpand && (
            <IconButton
              aria-label={`toggle ${name}`}
              size="small"
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={4}>
            <PolymarketEmbed name={name} slug={country.market} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PrizePoolBanner({
  playerCount,
  total,
}: {
  playerCount: number;
  total: number;
}): JSX.Element {
  return (
    <Paper
      sx={{
        alignItems: "center",
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        justifyContent: "space-between",
        p: 2,
      }}
    >
      <Box>
        <Typography component="span" variant="overline" sx={{ opacity: 0.85 }}>
          {winnersCopy.prizePoolLabel}
        </Typography>
        <Typography component="div">
          {playerCount} {winnersCopy.prizePoolUnit} × €{appConfig.winnerLotteryEntryFeeEur}
        </Typography>
      </Box>
      <Typography
        component="span"
        variant="h4"
        sx={{ color: "secondary.main", fontWeight: 800 }}
      >
        €{total}
      </Typography>
    </Paper>
  );
}

function parseSort(raw: string): WinnersSort {
  const [field, dir] = raw.split(":");
  if (
    SORT_FIELDS.includes(field as SortField) &&
    (dir === "asc" || dir === "desc")
  ) {
    return { field: field as SortField, dir };
  }
  return DEFAULT_SORT;
}

function sortWinners(
  countries: CountryRow[],
  odds: Map<string, number>,
  sort: WinnersSort,
): CountryRow[] {
  const factor = sort.dir === "asc" ? 1 : -1;
  const byName = (a: CountryRow, b: CountryRow): number =>
    (a.name ?? "").localeCompare(b.name ?? "");
  const yesPriceOf = (country: CountryRow): number | undefined =>
    country.market ? odds.get(country.market.trim()) : undefined;

  return [...countries].sort((a, b) => {
    if (sort.field === "country") {
      return factor * byName(a, b);
    }

    if (sort.field === "player") {
      const playerA = a.assignedTo?.trim() ?? "";
      const playerB = b.assignedTo?.trim() ?? "";
      if (!playerA && playerB) return 1; // unassigned last (both directions)
      if (playerA && !playerB) return -1;
      if (!playerA && !playerB) return byName(a, b);
      const cmp = playerA.localeCompare(playerB);
      return cmp !== 0 ? factor * cmp : byName(a, b);
    }

    // odds
    const oddsA = yesPriceOf(a);
    const oddsB = yesPriceOf(b);
    if (oddsA === undefined && oddsB === undefined) return byName(a, b);
    if (oddsA === undefined) return 1; // unknown last (both directions)
    if (oddsB === undefined) return -1;
    if (oddsA !== oddsB) return factor * (oddsA - oddsB);
    return byName(a, b);
  });
}
